import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Wallet, Plus, Pencil, X, Loader2, ChevronDown, XCircle,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchNatureOfGroups, fetchMasterGroups, fetchSubGroups, checkItCodeExists,
    fetchApprovalStatusIT, fetchItCodeGrid, submitNewItCode, submitEditItCode,
    clearExists, clearSaveResult, clearEditResult, clearMasterGroups, clearSubGroups, resetItCode,
    selectItCode,
} from '../../slices/accountsSlice/itCodeSlice';

// ── Shared primitives (matches AssignCCandDCABudget conventions) ───────────────
const inputCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';
const selectCls = inputCls + ' appearance-none pr-9 cursor-pointer';
const readonlyCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Btn = ({ onClick, disabled, loading, variant = 'primary', size = 'md', children, type = 'button' }) => {
    const base = 'inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed';
    const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm' };
    const variants = {
        primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
        secondary: 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400',
    };
    return (
        <button type={type} onClick={onClick} disabled={disabled || loading} className={`${base} ${sizes[size]} ${variants[variant]}`}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700',
        red: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-700',
        amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700',
        gray: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors[color] || colors.gray}`}>{children}</span>;
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const EmptyState = ({ message }) => <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">{message}</div>;

const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0 z-10">
                <h2 className="text-sm font-bold text-white">{title}</h2>
                <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
            <div className="p-5">{children}</div>
        </div>
    </div>
);

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDateForAPI = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// Grid dates come back as "dd-Mon-yyyy" (e.g. "01-Jan-2024") — CustomDatePicker's own
// string parser expects a numeric month, and native `new Date()` parsing of that format
// is unreliable across browsers, so parse it explicitly.
const parseDDMonYYYY = (str) => {
    if (!str || typeof str !== 'string') return null;
    const parts = str.split('-');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const monthIdx = MONTH_ABBR.findIndex((m) => m.toLowerCase() === parts[1].toLowerCase());
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || monthIdx === -1 || isNaN(year)) return null;
    return new Date(year, monthIdx, day);
};

const statusBadge = (status) => {
    if (status === 'Approved') return <Badge color="green">Approved</Badge>;
    if (status === 'Rejected') return <Badge color="red">Rejected</Badge>;
    return <Badge color="amber">Pending</Badge>;
};

// ═════════════════════════════════════════════════════════════════════════════
// ADD NEW IT CODE MODAL
// ═════════════════════════════════════════════════════════════════════════════

const AddITCodeModal = ({ onClose, currentUser, roleId, natureGroups, natureGroupsLoading }) => {
    const dispatch = useDispatch();
    const { masterGroups, masterGroupsLoading, subGroups, subGroupsLoading, exists, existsLoading, saveLoading, saveResult, saveError } = useSelector(selectItCode);

    const [itCode, setItCode] = useState('');
    const [itName, setItName] = useState('');
    const [natureGroupId, setNatureGroupId] = useState('');
    const [groupId, setGroupId] = useState('');
    const [subGroupId, setSubGroupId] = useState('');
    const [openingBalance, setOpeningBalance] = useState('');
    const [balanceAsOnDate, setBalanceAsOnDate] = useState(null);
    const [ledgerValueType, setLedgerValueType] = useState('');

    useEffect(() => {
        if (saveResult) { toast.success('IT Code added successfully.'); dispatch(clearSaveResult()); onClose(); }
        if (saveError) { toast.error(saveError); dispatch(clearSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError]);

    const handleItCodeBlur = () => {
        if (itCode.trim()) dispatch(checkItCodeExists(itCode.trim()));
        else dispatch(clearExists());
    };

    const handleNatureChange = (val) => {
        setNatureGroupId(val); setGroupId(''); setSubGroupId('');
        dispatch(clearMasterGroups());
        if (val) dispatch(fetchMasterGroups(val));
    };

    const handleGroupChange = (val) => {
        setGroupId(val); setSubGroupId('');
        dispatch(clearSubGroups());
        if (val) dispatch(fetchSubGroups(val));
    };

    const handleSubmit = () => {
        if (!itCode.trim()) { toast.warning('Enter IT Code.'); return; }
        if (exists === true) { toast.warning('IT Code already exists.'); return; }
        if (!itName.trim()) { toast.warning('Enter IT Code Name.'); return; }
        if (!natureGroupId) { toast.warning('Select Nature of Group.'); return; }
        if (!groupId) { toast.warning('Select Group.'); return; }
        if (subGroups.length > 0 && !subGroupId) { toast.warning('Select Sub Group.'); return; }
        if (!openingBalance || isNaN(parseFloat(openingBalance))) { toast.warning('Enter Opening Balance.'); return; }
        if (!balanceAsOnDate) { toast.warning('Select Balance As On Date.'); return; }
        if (!ledgerValueType) { toast.warning('Select Ledger Value Type.'); return; }

        dispatch(submitNewItCode({
            Itid: 0,
            ItCode: itCode.trim(),
            ItName: itName.trim(),
            Action: 'Add',
            CreatedBy: currentUser,
            RoleId: parseInt(roleId, 10) || 0,
            NatureGroupId: natureGroupId,
            GroupId: groupId,
            SubGroupId: subGroupId ? parseInt(subGroupId, 10) : 0,
            LedgerValueType: ledgerValueType,
            OpeningBalance: parseFloat(openingBalance),
            BalanceAsOnDate: formatDateForAPI(balanceAsOnDate),
        }));
    };

    return (
        <Modal title="Add New IT Code" onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label required>IT Code</Label>
                    <input className={inputCls} value={itCode} onChange={(e) => setItCode(e.target.value)} onBlur={handleItCodeBlur} disabled={saveLoading} />
                    {existsLoading && <p className="text-xs text-gray-400 mt-1">Checking…</p>}
                    {!existsLoading && exists === true && <p className="text-xs text-rose-500 mt-1">IT Code already exists</p>}
                    {!existsLoading && exists === false && <p className="text-xs text-emerald-600 mt-1">IT Code is available</p>}
                </div>
                <div>
                    <Label required>IT Code Name</Label>
                    <input className={inputCls} value={itName} onChange={(e) => setItName(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label required>Nature Of Group</Label>
                    <SelectWrap loading={natureGroupsLoading}>
                        <select className={selectCls} value={natureGroupId} onChange={(e) => handleNatureChange(e.target.value)} disabled={saveLoading}>
                            <option value="">-Please Select-</option>
                            {natureGroups.map((n) => <option key={n.NatureGroupId} value={n.NatureGroupId}>{n.NatureGroupName}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Master Group</Label>
                    <SelectWrap loading={masterGroupsLoading}>
                        <select className={selectCls} value={groupId} onChange={(e) => handleGroupChange(e.target.value)} disabled={saveLoading || !natureGroupId}>
                            <option value="">-Please Select-</option>
                            {masterGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                {subGroups.length > 0 && (
                    <div>
                        <Label required>Sub Group</Label>
                        <SelectWrap loading={subGroupsLoading}>
                            <select className={selectCls} value={subGroupId} onChange={(e) => setSubGroupId(e.target.value)} disabled={saveLoading}>
                                <option value="">-Please Select-</option>
                                {subGroups.map((s) => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                )}
                <div>
                    <Label required>Opening Balance</Label>
                    <input className={inputCls} value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} disabled={saveLoading} inputMode="decimal" />
                </div>
                <div>
                    <Label required>Balance As On Date</Label>
                    <CustomDatePicker value={balanceAsOnDate} onChange={setBalanceAsOnDate} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} />
                </div>
                <div className="md:col-span-2">
                    <Label required>Ledger Value Type</Label>
                    <div className="flex gap-4 mt-1">
                        {['Credit', 'Debit'].map((t) => (
                            <label key={t} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                <input type="radio" name="ledgerValueType" checked={ledgerValueType === t} onChange={() => setLedgerValueType(t)} disabled={saveLoading} />
                                {t}
                            </label>
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={saveLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={saveLoading}>Add</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// EDIT IT CODE MODAL
// ═════════════════════════════════════════════════════════════════════════════

const EditITCodeModal = ({ row, onClose, currentUser, roleId }) => {
    const dispatch = useDispatch();
    const { editLoading, editResult, editError } = useSelector(selectItCode);

    const hasLedger = row.LedgerName !== null && row.LedgerName !== undefined && row.LedgerName !== '';

    const [itName, setItName] = useState(row.ItName || '');
    const [openingBalance, setOpeningBalance] = useState(row.OpeningBalance ?? '');
    const [balanceAsOnDate, setBalanceAsOnDate] = useState(parseDDMonYYYY(row.BalanceAsOnDate));
    const [ledgerValueType, setLedgerValueType] = useState(row.LedgerValueType || '');

    useEffect(() => {
        if (editResult) { toast.success('IT Code updated successfully.'); dispatch(clearEditResult()); onClose(); }
        if (editError) { toast.error(editError); dispatch(clearEditResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editResult, editError]);

    const handleSubmit = () => {
        if (!itName.trim()) { toast.warning('Enter IT Name.'); return; }

        dispatch(submitEditItCode({
            Itid: row.Itid,
            ItCode: row.ItCode,
            ItName: itName.trim(),
            Action: 'Update',
            CheckUpdationType: 'Insert',
            CreatedBy: currentUser,
            RoleId: parseInt(roleId, 10) || 0,
            NatureGroupId: row.NatureGroupId,
            GroupId: row.GroupId,
            SubGroupId: row.SubGroupId,
            LedgerValueType: hasLedger ? ledgerValueType : null,
            OpeningBalance: hasLedger ? parseFloat(openingBalance) || 0 : 0,
            BalanceAsOnDate: hasLedger ? formatDateForAPI(balanceAsOnDate) : null,
        }));
    };

    return (
        <Modal title={`Edit IT Code — ${row.ItCode}`} onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label>IT Code</Label>
                    <input className={readonlyCls} value={row.ItCode} readOnly />
                </div>
                <div>
                    <Label>Nature Of Group</Label>
                    <input className={readonlyCls} value={row.NatureGroupName || ''} readOnly />
                </div>
                <div>
                    <Label required>IT Code Name</Label>
                    <input className={inputCls} value={itName} onChange={(e) => setItName(e.target.value)} disabled={editLoading} />
                </div>
                <div>
                    <Label>Group Name</Label>
                    <input className={readonlyCls} value={row.GroupName || ''} readOnly />
                </div>
                {row.SubGroupId ? (
                    <div>
                        <Label>Sub Group Name</Label>
                        <input className={readonlyCls} value={row.SubGroupName || ''} readOnly />
                    </div>
                ) : null}

                {hasLedger && (
                    <>
                        <div>
                            <Label required>Opening Balance</Label>
                            <input className={inputCls} value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} disabled={editLoading} inputMode="decimal" />
                        </div>
                        <div>
                            <Label required>Balance As On Date</Label>
                            <CustomDatePicker value={balanceAsOnDate} onChange={setBalanceAsOnDate} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} />
                        </div>
                        <div className="md:col-span-2">
                            <Label required>Ledger Value Type</Label>
                            <div className="flex gap-4 mt-1">
                                {['Credit', 'Debit'].map((t) => (
                                    <label key={t} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                        <input type="radio" name="editLedgerValueType" checked={ledgerValueType === t} onChange={() => setLedgerValueType(t)} disabled={editLoading} />
                                        {t}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={editLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={editLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const AddNewITCode = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const currentUser = userData?.userName || userData?.userId || userData?.UID || userData?.uid || '';

    const { natureGroups, natureGroupsLoading, grid, gridLoading, gridError, allowedActions } = useSelector(selectItCode);

    const [showAdd, setShowAdd] = useState(false);
    const [editRow, setEditRow] = useState(null);

    useEffect(() => {
        dispatch(fetchNatureOfGroups());
        dispatch(fetchItCodeGrid());
        dispatch(fetchApprovalStatusIT(roleId));
        return () => { dispatch(resetItCode()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const canAct = (row) => row.ITAPRStatus === 'Approved' && (!row.CloseStatus || row.CloseStatus === 'Rejected');

    const handleActionChange = (row, action) => {
        if (!action || action === 'Select' || action === '0') return;
        if (action === 'Edit') { setEditRow(row); return; }
        toast.info(`"${action}" isn't available on this page yet.`);
    };

    const refreshGrid = () => dispatch(fetchItCodeGrid());

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Wallet className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">{menuData?.name || 'IT Codes'}</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">Create and manage IT (Income/Expenditure) Codes</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
                <Card>
                    <div className="p-4 flex flex-wrap items-center justify-end gap-3">
                        <Btn onClick={() => setShowAdd(true)}><Plus className="h-3.5 w-3.5" />Add IT</Btn>
                    </div>
                </Card>

                <Card>
                    {gridLoading ? (
                        <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                    ) : gridError ? (
                        <div className="py-10 text-center text-sm text-rose-500">{gridError}</div>
                    ) : grid.length === 0 ? (
                        <EmptyState message="No IT Codes found." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        {['IT Code', 'IT Name', 'Nature Of Group', 'Group', 'Status', ''].map((h) => (
                                            <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {grid.map((row) => (
                                        <tr key={row.Itid} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                            <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{row.ItCode}</td>
                                            <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{row.ItName}</td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.NatureGroupName}</td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.GroupName}{row.SubGroupName ? ` / ${row.SubGroupName}` : ''}</td>
                                            <td className="px-3 py-2.5">{statusBadge(row.ITAPRStatus)}</td>
                                            <td className="px-3 py-2.5">
                                                {canAct(row) ? (
                                                    allowedActions.length > 0 ? (
                                                        <SelectWrap>
                                                            <select
                                                                className={selectCls + ' py-1 text-xs'}
                                                                defaultValue=""
                                                                onChange={(e) => { handleActionChange(row, e.target.value); e.target.value = ''; }}
                                                            >
                                                                <option value="">-Select-</option>
                                                                {allowedActions.map((a, i) => <option key={i} value={a.Value}>{a.Text}</option>)}
                                                            </select>
                                                        </SelectWrap>
                                                    ) : (
                                                        <button onClick={() => setEditRow(row)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Edit">
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                    )
                                                ) : (
                                                    <span className="text-xs text-gray-300 dark:text-gray-600 inline-flex items-center gap-1">
                                                        <XCircle className="h-3 w-3" /> —
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {showAdd && (
                <AddITCodeModal
                    onClose={() => { setShowAdd(false); refreshGrid(); }}
                    currentUser={currentUser} roleId={roleId}
                    natureGroups={natureGroups} natureGroupsLoading={natureGroupsLoading}
                />
            )}
            {editRow && (
                <EditITCodeModal
                    row={editRow} onClose={() => { setEditRow(null); refreshGrid(); }}
                    currentUser={currentUser} roleId={roleId}
                />
            )}
        </div>
    );
};

export default AddNewITCode;
